<template>
  <div class="orgchart-view" v-loading="loading">
    <div class="flex justify-between items-center mb-4">
      <el-breadcrumb separator-class="uil uil-angle-right-b m-breadcrum">
        <el-breadcrumb-item :to="{ name: 'SettingsUserIndex' }">
          <i class="uil uil-user"></i> Users
        </el-breadcrumb-item>
        <el-breadcrumb-item>Org Chart</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <!-- Org Chart Card -->
    <div class="card">
      <div class="card-title">Organization Chart</div>
      <div class="card-subtitle">Reporting hierarchy based on the "Reports To" field on each user.</div>

      <div v-if="!loading && trees.length === 0 && orphans.length === 0" class="text-gray-500 py-4">
        No reporting relationships configured. Set "Reports To" on user profiles to build the hierarchy.
      </div>

      <div v-show="!loading && trees.length > 0" id="orgchart-root" class="orgchart-container"></div>
    </div>

    <!-- Unassigned Users Table Card -->
    <div v-if="!loading && orphans.length > 0" class="card mt-4">
      <div class="card-title">Users without a manager or subordinates</div>
      <div class="card-subtitle">These users are not part of the reporting hierarchy. Assign a "Reports To" on their profile to add them to the org chart.</div>

      <el-table :data="orphans" style="width: 100%" @row-click="navigateToUser" class="orphan-table">
        <el-table-column label="Name" min-width="200">
          <template slot-scope="{ row }">
            <span class="font-semibold text-sm">{{ row.firstName }} {{ row.lastName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="email" label="Email" min-width="250">
          <template slot-scope="{ row }">
            <span class="text-gray-500 text-sm">{{ row.email }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Access" width="140">
          <template slot-scope="{ row }">
            <el-tag v-if="!row.permissionLogin" size="mini" type="info">Inactive</el-tag>
            <el-tag v-if="row.permissionFullAccess" size="mini" type="success">Full Access</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Assign Manager" width="220">
          <template slot-scope="{ row }">
            <div @click.stop>
              <el-select
                :value="row.reportsToUserId"
                placeholder="Select manager"
                size="small"
                filterable
                clearable
                :loading="savingUserId === row.id"
                @change="(val) => assignManager(row, val)"
              >
                <el-option
                  v-for="manager in managerOptionsFor(row)"
                  :key="manager.id"
                  :label="`${manager.firstName} ${manager.lastName}`"
                  :value="manager.id"
                />
              </el-select>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { listUsers, getTenant, staffReportingHierarchy } from './query'
import { updateUser } from '@/store/mutations'
import OrgChart from 'orgchart.js'
import 'orgchart.js/src/orgchart.css'
import { buildActiveManagerHierarchy, getAvailableManagerUsers, isOutsideActiveHierarchy, validateReportsToSelection } from './hierarchyUtils'

export default {
  name: 'OrgChartView',
  data() {
    return {
      users: [],
      ownerUserId: null,
      staffSubordinateCounts: {},
      loading: true,
      chartInstance: null,
      savingUserId: null,
    }
  },
  computed: {
    ...mapState(['userInfo']),

    activeUsers() {
      return this.users.filter(u => u.permissionLogin)
    },

    userMap() {
      const map = new Map()
      for (const user of this.users) {
        map.set(user.id, user)
      }
      return map
    },

    hierarchyData() {
      return buildActiveManagerHierarchy(this.users)
    },

    trees() {
      const { childrenMap, hasParent } = this.hierarchyData

      const ownerUserId = this.ownerUserId
      const subordinateCounts = this.staffSubordinateCounts

      const pickNodeFields = (user) => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        permissionFullAccess: user.permissionFullAccess,
        permissionLogin: user.permissionLogin,
        isOwner: user.id === ownerUserId,
        isAlsoAssociate: !!user.samePersonAsStaffId,
        associateSubordinateCount: user.samePersonAsStaffId
          ? (subordinateCounts[user.samePersonAsStaffId] || 0) : 0,
        group: user.group,
        name: `${user.firstName} ${user.lastName}`,
      })

      const buildTree = (user) => {
        const kids = (childrenMap.get(user.id) || [])
          .sort((a, b) => a.firstName.localeCompare(b.firstName))
          .map(buildTree)
        const node = pickNodeFields(user)
        if (kids.length) node.children = kids
        return node
      }

      // Root nodes: no parent AND (has children OR is the owner)
      return this.users
        .filter(u => !hasParent.has(u.id) && (childrenMap.has(u.id) || u.id === ownerUserId))
        .sort((a, b) => {
          // Owner always first
          if (a.id === ownerUserId) return -1
          if (b.id === ownerUserId) return 1
          return a.firstName.localeCompare(b.firstName)
        })
        .map(buildTree)
    },

    orphans() {
      const { hasParent, hasChildren } = this.hierarchyData
      const hierarchy = { hasParent, hasChildren }

      return this.users
        .filter(u => isOutsideActiveHierarchy(u, hierarchy, this.ownerUserId))
        .sort((a, b) => a.firstName.localeCompare(b.firstName))
    },

    sortedManagerOptions() {
      return [...this.activeUsers].sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
      )
    }
  },
  mounted() {
    this.loadData()
  },
  beforeDestroy() {
    this.destroyChart()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const group = this.userInfo.tenant.group
        const [result, tenantResult, staffResult] = await Promise.all([
          this.gLoadListAll(listUsers, { filter: { group: { contains: group } }, limit: 1000 }, 'listUsers'),
          this.api(getTenant, { id: this.userInfo.tenant.id }),
          this.gLoadListAll(staffReportingHierarchy, { group, limit: 1000 }, 'staffsByGroup')
        ])
        this.users = result
          .filter(user => !(user.email.startsWith('sysadmin-') && user.email.endsWith('@herasolutions.info')))
        this.ownerUserId = tenantResult?.data?.getTenant?.ownerUserId || null

        // Count direct associate subordinates per staff ID
        const counts = {}
        for (const staff of staffResult) {
          if (staff.reportsToStaffId) {
            counts[staff.reportsToStaffId] = (counts[staff.reportsToStaffId] || 0) + 1
          }
        }
        this.staffSubordinateCounts = counts
      } catch (e) {
        console.error(e)
        this.displayUserError(e)
      } finally {
        this.loading = false
        this.$nextTick(() => this.renderChart())
      }
    },

    destroyChart() {
      if (this.chartInstance) {
        const container = document.getElementById('orgchart-root')
        if (container) container.innerHTML = ''
        this.chartInstance = null
      }
    },

    renderChart() {
      if (!this.trees.length) return
      this.destroyChart()

      const chartData = this.trees.length === 1
        ? this.trees[0]
        : { id: 'root', name: 'Organization', className: 'hidden-root', children: this.trees }

      const vm = this

      this.chartInstance = new OrgChart({
        chartContainer: '#orgchart-root',
        data: chartData,
        nodeTitle: 'name',
        nodeContent: 'email',
        nodeId: 'id',
        pan: false,
        zoom: false,
        depth: 999,
        createNode(nodeDiv, nodeData) {
          if (nodeData.className === 'hidden-root') return

          const controls = Array.from(nodeDiv.querySelectorAll(':scope > .edge, :scope > .toggleBtn'))

          const card = document.createElement('div')
          card.className = 'oc-node-card'

          const nameEl = document.createElement('div')
          nameEl.className = 'oc-node-name'
          nameEl.textContent = (nodeData.firstName || '') + ' ' + (nodeData.lastName || '')
          card.appendChild(nameEl)

          const emailEl = document.createElement('div')
          emailEl.className = 'oc-node-email'
          emailEl.textContent = nodeData.email || ''
          card.appendChild(emailEl)

          if (nodeData.isOwner || nodeData.permissionFullAccess || nodeData.isAlsoAssociate || !nodeData.permissionLogin) {
            const badges = document.createElement('div')
            badges.className = 'oc-node-badges'
            if (!nodeData.permissionLogin) {
              const badge = document.createElement('span')
              badge.className = 'oc-badge oc-badge--inactive'
              badge.textContent = 'Inactive'
              badges.appendChild(badge)
            }
            if (nodeData.isOwner) {
              const ownerBadge = document.createElement('span')
              ownerBadge.className = 'oc-badge oc-badge--owner'
              ownerBadge.textContent = 'Owner'
              badges.appendChild(ownerBadge)
            }
            if (nodeData.permissionFullAccess) {
              const badge = document.createElement('span')
              badge.className = 'oc-badge oc-badge--admin'
              badge.textContent = 'Full Access'
              badges.appendChild(badge)
            }
            if (nodeData.isAlsoAssociate) {
              const badge = document.createElement('span')
              badge.className = 'oc-badge oc-badge--associate'
              badge.textContent = 'Also an Associate'
              badges.appendChild(badge)
            }
            card.appendChild(badges)
          }

          if (nodeData.associateSubordinateCount > 0) {
            const subLine = document.createElement('div')
            subLine.className = 'oc-subordinate-count'
            subLine.textContent = '+ ' + nodeData.associateSubordinateCount
              + ' Associate' + (nodeData.associateSubordinateCount === 1 ? '' : 's')
            card.appendChild(subLine)
          }

          nodeDiv.innerHTML = ''
          nodeDiv.appendChild(card)
          controls.forEach(control => nodeDiv.appendChild(control))

          nodeDiv.style.cursor = 'pointer'
          nodeDiv.addEventListener('click', function (e) {
            const controlTarget = e.target.closest && e.target.closest('.edge, .toggleBtn')
            if (controlTarget) {
              e.stopPropagation()
              return
            }
            e.stopPropagation()
            vm.navigateToUser(nodeData)
          })
        }
      })
    },

    sortedManagerOptionsFiltered(userId) {
      return this.sortedManagerOptions.filter(u => u.id !== userId)
    },

    managerOptionsFor(user) {
      return getAvailableManagerUsers(this.users, user.id).sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`)
      )
    },

    async assignManager(user, managerId) {
      const validation = validateReportsToSelection(this.users, user.id, managerId)
      if (!validation.valid) {
        this.displayUserError(validation.message)
        return
      }

      this.savingUserId = user.id
      try {
        const input = { id: user.id, reportsToUserId: managerId || null }
        await this.api(updateUser, { input })

        const idx = this.users.findIndex(u => u.id === user.id)
        if (idx !== -1) {
          this.$set(this.users, idx, { ...this.users[idx], reportsToUserId: managerId || null })
        }

        this.$nextTick(() => this.renderChart())
        const manager = managerId ? this.userMap.get(managerId) : null
        this.displayUserNotification({
          title: 'Success',
          type: 'success',
          message: managerId
            ? `${user.firstName} ${user.lastName} now reports to ${manager ? manager.firstName : 'selected manager'}`
            : `Removed manager for ${user.firstName} ${user.lastName}`
        })
      } catch (e) {
        console.error(e)
        this.displayUserError(e)
      } finally {
        this.savingUserId = null
      }
    },

    navigateToUser(user) {
      this.$router.push({
        name: 'SettingsUserDetail',
        query: {
          id: user.id,
          tenantId: this.userInfo.tenant.id,
          group: this.userInfo.tenant.group,
          companyName: this.userInfo.tenant.companyName
        }
      })
    }
  }
}
</script>

<style lang="scss">
.orgchart-view .orgchart-container {
  overflow: auto;
  min-height: 300px;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  margin-top: 1rem;
  position: relative;
  background: #fafbfc;

  .orgchart {
    background-image: none;
    padding: 30px 20px;

    // Hide the virtual root node when there are multiple trees
    .node.hidden-root {
      visibility: hidden;
      height: 0;
      padding: 0;
      margin: 0;
      overflow: hidden;

      & > .title,
      & > .content {
        display: none;
      }
    }

    // Override default node styling
    .node {
      width: auto;
      min-width: 180px;
      max-width: 260px;
      padding: 0;
      border: none;
      border-radius: 0;

      &:hover {
        background-color: transparent;
      }

      .title,
      .content {
        display: none;
      }
    }

    .oc-node-card {
      background: #fff;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px 14px;
      text-align: left;
      transition: box-shadow 0.15s ease, border-color 0.15s ease;

      &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        border-color: #3b82f6;
      }
    }

    .oc-node-name {
      font-weight: 600;
      font-size: 0.875rem;
      color: #1f2937;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .oc-node-email {
      font-size: 0.75rem;
      color: #6b7280;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: 2px;
    }

    .oc-node-badges {
      display: flex;
      gap: 4px;
      margin-top: 4px;
    }

    .oc-badge {
      font-size: 0.625rem;
      font-weight: 700;
      padding: 2px 8px;
      border-radius: 9999px;
      text-transform: uppercase;
    }

    .oc-badge--admin {
      background: rgba(16, 185, 129, 0.1);
      color: #059669;
    }

    .oc-badge--associate {
      background: rgba(245, 158, 11, 0.1);
      color: #d97706;
    }

    .oc-badge--inactive {
      background: rgba(107, 114, 128, 0.12);
      color: #4b5563;
    }

    .oc-subordinate-count {
      font-size: 0.6875rem;
      font-weight: 600;
      color: #9ca3af;
      background: #f3f4f6;
      border-radius: 4px;
      padding: 2px 8px;
      margin-top: 5px;
      text-align: center;
    }

    // Restyle connector lines to match app theme
    tr.lines .topLine {
      border-top: 2px solid #cbd5e1;
    }

    tr.lines .rightLine {
      border-right: 1px solid #cbd5e1;
    }

    tr.lines .leftLine {
      border-left: 1px solid #cbd5e1;
    }

    tr.lines .downLine {
      background-color: #cbd5e1;
    }

    .edge {
      color: #64748b;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 16px;
      z-index: 2;

      &::before {
        content: '';
        display: inline-block;
        width: 7px;
        height: 7px;
        border: solid currentColor;
        border-width: 0 2px 2px 0;
      }

      &.verticalEdge {
        height: 18px;
      }

      &.horizontalEdge {
        width: 18px;
      }

      &.fa-chevron-up::before {
        transform: rotate(-135deg);
      }

      &.fa-chevron-down::before {
        transform: rotate(45deg);
      }

      &.fa-chevron-left::before {
        transform: rotate(135deg);
      }

      &.fa-chevron-right::before {
        transform: rotate(-45deg);
      }

      &:hover {
        color: #2563eb;
      }
    }
  }
}

.orgchart-view .orphan-table {
  .el-table__row {
    cursor: pointer;

    &:hover > td {
      background-color: #f8fafc;
    }
  }
}
</style>
